import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { AutoComplete, AutoCompleteOption } from '@wayste/sour-ui';
import { formatServiceAddress } from '@wayste/utils';
import { Loader } from '@googlemaps/js-api-loader';
import debounce from 'lodash.debounce';
import { useFormContext } from 'react-hook-form';
/**
 * GooglePlacesInput component integrates Google Places Autocomplete allowing users to search for places and select a location.
 * The properties for configuring this component can be found at {@link GooglePlacesInputProps}
 */
export function GooglePlacesInput({ fieldName, apiKey, onPlaceSelected, onError, inputProps, required = false, partial = false, disabled = false, showFullAddressInInput = false, searchOptions, showCountry, }) {
    const [loadingResults, setLoadingResults] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const loader = new Loader({
        apiKey: apiKey,
        version: 'weekly',
        libraries: ['places', 'core', 'geocoding'],
        id: 'google-maps-script',
    });
    const places = useRef();
    useEffect(() => {
        setIsLoaded(false);
        (async () => {
            places.current = await loader.importLibrary('places');
            setIsLoaded(true);
        })();
    }, []);
    const [hasValue, setHasValue] = useState(false);
    const [displayValue, setDisplayValue] = useState(inputProps?.queryValue || ''); //only for rendering SELECTED values, not actual live query value
    const [suggestions, setSuggestions] = useState([]);
    const methods = useFormContext();
    const emptyAddress = {
        address: {
            addressLine1: '',
            city: '',
            state: '',
            zip: '',
            country: '',
        },
        coordinates: {
            lat: 0,
            lng: 0,
        },
        county: '',
    };
    const countyCleaner = (cleanAddress, county) => {
        if (cleanAddress.city === 'Baltimore') {
            return 'Baltimore City';
        }
        else if (cleanAddress.state === 'DC') {
            return 'District of Columbia';
        }
        else if (!county) {
            return '';
        }
        else {
            return county?.longText?.replace(' County', '') || '';
        }
    };
    const handleSelect = async (predictionID) => {
        if (predictionID === null) {
            setDisplayValue('');
            onPlaceSelected(emptyAddress, true);
            return;
        }
        const suggestion = suggestions.find((prediction) => prediction.placePrediction?.placeId === predictionID);
        if (!suggestion) {
            if (inputProps?.allowCustomValues) {
                setDisplayValue(predictionID);
                onPlaceSelected({ ...emptyAddress, name: predictionID, address: { ...emptyAddress.address, addressLine1: predictionID } });
                return;
            }
            else {
                console.warn('The user selected a suggestion that does not exist.');
                setDisplayValue('');
                onPlaceSelected(emptyAddress, true);
                return;
            }
        }
        const foundPlace = (await suggestion.placePrediction.toPlace().fetchFields({
            fields: ['displayName', 'formattedAddress', 'addressComponents'],
        })).place;
        if (!foundPlace) {
            console.error('Prediction not found');
            return;
        }
        try {
            const { place } = await foundPlace.fetchFields({
                fields: ['displayName', 'formattedAddress', 'addressComponents', 'location'],
            });
            // ERROR CHECKS
            if (!place.addressComponents) {
                console.error('Address components were unable to be grabbed');
                if (onError)
                    onError({ type: 'NOT_FOUND', message: 'No address components found' });
                setDisplayValue('');
                onPlaceSelected(emptyAddress, true);
                return;
            }
            // Check it has a street number
            if (!partial && !place.addressComponents.find((component) => component.types.includes('street_number'))) {
                onError?.({
                    type: 'BAD_INPUT',
                    message: 'Please enter a valid address. It must include a street number.',
                    address: formatAsAddress(place.addressComponents),
                    coordinates: place.location?.toJSON(),
                });
                setDisplayValue('');
                onPlaceSelected(emptyAddress, true);
                return;
            }
            // Check it has a valid lat/lng
            const lat = place.location?.lat();
            const lng = place.location?.lng();
            if (!lat || !lng) {
                console.error('Lat or Lng not found');
                if (onError)
                    onError({ type: 'NOT_SUPPORTED', message: 'This location is not supported. Please try another address.' });
                setDisplayValue('');
                onPlaceSelected(emptyAddress, true);
                return;
            }
            // Clean address data
            const cleanAddress = formatAsAddress(place.addressComponents);
            const county = place.addressComponents.find((component) => component.types.includes('administrative_area_level_2'));
            const cleanCounty = countyCleaner(cleanAddress, county);
            const formattedAddress = formatServiceAddress(cleanAddress, { partial, withCountry: showCountry });
            // Set appropriate variables
            const serviceLocation = {
                address: cleanAddress,
                coordinates: {
                    lat: lat,
                    lng: lng,
                },
                county: cleanCounty || '',
            };
            if (fieldName)
                methods.setValue(fieldName, formattedAddress);
            if (showFullAddressInInput) {
                setDisplayValue(formattedAddress);
            }
            else {
                setDisplayValue(cleanAddress.addressLine1);
            }
            onPlaceSelected(serviceLocation);
        }
        catch (error) {
            console.error('Error fetching place predictions:', error);
            if (onError)
                onError({ type: 'NOT_SUPPORTED', message: 'This location is not supported. Please try another address.' });
            setDisplayValue('');
            onPlaceSelected(emptyAddress, true);
        }
    };
    const formatAsAddress = (addressComponents) => {
        const address = addressComponents.reduce((acc, component) => {
            if (component.types.includes('street_number')) {
                acc.addressLine1 = component.longText || '';
            }
            if (component.types.includes('route')) {
                if (acc.addressLine1) {
                    acc.addressLine1 += ` ${component.longText}`;
                }
                else {
                    acc.addressLine1 = component.longText || '';
                }
            }
            if (component.types.includes('locality')) {
                acc.city = component.longText || '';
            }
            else if (component.types.includes('neighborhood') && !acc.city) {
                acc.city = component.longText || '';
            }
            else if (component.types.includes('sublocality_level_1') && !acc.city) {
                acc.city = component.longText || '';
            }
            else if (component.types.includes('administrative_area_level_3') && !acc.city) {
                acc.city = component.longText || '';
            }
            if (component.types.includes('administrative_area_level_1')) {
                acc.state = component.shortText || '';
            }
            if (component.types.includes('postal_code')) {
                acc.zip = component.longText || '';
            }
            if (component.types.includes('country')) {
                acc.country = component.shortText || '';
            }
            if (!acc.addressLine1)
                acc.addressLine1 = '';
            if (!acc.city)
                acc.city = '';
            if (!acc.zip)
                acc.zip = '';
            return acc;
        }, {});
        return address;
    };
    const loadPredictions = useCallback(async (rawInput) => {
        const sanitizedInput = rawInput.trim();
        setHasValue(sanitizedInput.length > 3);
        if (sanitizedInput.length === 0) {
            setSuggestions([]);
            return;
        }
        try {
            if (!isLoaded || !places.current) {
                places.current = await loader.importLibrary('places');
            }
            setLoadingResults(true);
            const { AutocompleteSessionToken, AutocompleteSuggestion } = places.current;
            // Add an initial request body.
            const request = {
                input: sanitizedInput,
                language: 'en-US',
                region: 'us',
                includedRegionCodes: ['US'],
                ...searchOptions,
            };
            // Create a session token.
            const token = new AutocompleteSessionToken();
            // Add the token to the request.
            request.sessionToken = token;
            // Fetch autocomplete suggestions.
            const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            setSuggestions(suggestions);
        }
        catch (error) {
            console.error('Error fetching place predictions:', error);
        }
        finally {
            setLoadingResults(false);
        }
    }, []);
    const debouncedLoadPredictions = useCallback(debounce(loadPredictions, 200), [loadPredictions]);
    const handleInputChange = (event) => {
        debouncedLoadPredictions(event);
    };
    useEffect(() => {
        if (inputProps?.queryValue) {
            setDisplayValue(inputProps.queryValue);
            debouncedLoadPredictions(inputProps.queryValue);
        }
    }, [inputProps?.queryValue]);
    return (_jsx(AutoComplete, { "data-testid": "google-places-input", label: inputProps?.label ?? 'Address', required: required, onQueryChange: (value) => handleInputChange(value), onSelect: (value) => handleSelect(value), disabled: disabled || !isLoaded, renderLabel: displayValue, externalFilter: true, loading: loadingResults || !isLoaded, allowChangeWhenLoading: isLoaded, hideClearButton: true, ...inputProps, children: suggestions.length === 0 && hasValue
            ? [
                _jsx(AutoCompleteOption, { value: "", disabled: true, children: "No results found" }, "noPredictions"),
            ]
            : suggestions
                .filter((pred) => !!pred.placePrediction?.placeId)
                .map((prediction) => (_jsx(AutoCompleteOption, { value: prediction.placePrediction.placeId, children: `${prediction.placePrediction?.mainText?.text} ${prediction.placePrediction?.secondaryText?.text}` }, prediction.placePrediction?.placeId))) }));
}
//# sourceMappingURL=places-input.js.map